<template>
  <div class="cookie-banner">
    <div class="banner-container" role="region" aria-label="Cookie Banner">
      <div class="message-container">
        <p class="message-text">
          {{ message }}
        </p>
        <a :href="cookieUrl">
          {{ urlLabel }}
          <Icon name="cta-arrow" :color="$config.colors.blue" />
        </a>
      </div>
      <div :class="['options-container', { 'button-outline': !isMobileView }]">
        <Button class="secondary" @click="permitCookies(false)">Decline</Button>
        <Button class="primary" autofocus @click="permitCookies(true)">Allow Cookies</Button>
      </div>
    </div>
  </div>
</template>

<script>
import { likeshopCopy } from '@/config/messages';
import { browserStorageSetItem, getCookieConsentKey } from '@/utils/browserStorage';
import { isMobileClient } from '@/utils/mobile';

import Button from '@/components/Button.vue';
import Icon from '@/components/Icon.vue';

export default {
  components: {
    Button,
    Icon,
  },
  props: {
    platformType: {
      type: String,
      required: true,
    },
    socialMediaAccountName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      message: likeshopCopy.cookiePolicyMessage,
      urlLabel: likeshopCopy.cookiePolicyUrlLabel,
      cookieUrl: 'https://dashsocial.com/cookies/',
    };
  },
  computed: {
    isMobileView() {
      return isMobileClient();
    },
  },
  methods: {
    permitCookies(val) {
      const key = getCookieConsentKey(this.platformType, this.socialMediaAccountName);

      if (key) {
        browserStorageSetItem(key, val);
        this.$emit('allowCookies', val);
      }
    },
  },
};
</script>

<style scoped lang="postcss">
.cookie-banner {
  background-color: white;
  bottom: 0;
  position: fixed;
  width: 100%;
  z-index: var(--z-index-raised);
  justify-content: center;

  .banner-container {
    margin: 0 auto;

    .message-container {
      font-size: var(--font-size-12);
      line-height: 15px;
      margin: var(--space-16) auto;
      max-width: 300px;
      text-align: center;

      .message-text {
        display: inline;
      }

      a {
        color: var(--header-text-black);
        text-decoration: underline;
      }

      :deep(.icon) {
        display: none;
      }
    }

    .options-container {
      display: flex;
      justify-content: center;
      margin-bottom: var(--space-16);

      button {
        background: var(--white);
        border-radius: 1rem;
        font-size: var(--font-size-12);
        font-weight: 600;
        height: 26px;
        margin: 0 var(--space-4);
        padding: 0;
        width: 120px;

        &.primary {
          border: 1.6px solid var(--blue);
          color: var(--blue);
        }

        &.secondary {
          border: 1.6px solid var(--secondary-text-icon);
          color: var(--secondary-text-icon);
        }
      }

      &.button-outline button {
        &:focus {
          box-shadow: 0 0 3px 2px var(--link);
          outline: none;
        }
      }
    }
  }
}

@media (--mobile-landscape) {
  .cookie-banner {
    display: flex;
    height: 4rem;

    .banner-container {
      align-items: center;
      display: flex;
      justify-content: space-between;
      max-width: 916px;
      width: 100%;

      .message-container {
        display: flex;
        font-size: var(--font-size-14);
        margin-left: var(--space-16);

        a {
          align-items: center;
          color: var(--blue);
          display: flex;
          margin-left: var(--space-8);
          text-decoration: none;

          :deep(.icon) {
            margin-left: 12px;
          }
        }
      }

      .options-container {
        margin: 0 var(--space-16) 0 0;

        button {
          font-size: var(--font-size-14);
          height: 32px;

          &.secondary {
            border: none;
            width: 64px;
          }
        }
      }
    }
  }
}
</style>
